import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { CreditCard, DollarSign, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

export default function StripePaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [stats, setStats] = useState({ total: 0, paid: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [verifyingPaymentId, setVerifyingPaymentId] = useState(null);

  useEffect(() => {
    fetchPayments();
    fetchStats();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await axios.get('/admin/payments/all');
      setPayments(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch payments', error);
      toast.error('Errore nel caricamento pagamenti');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/admin/payments/stats');
      const data = response.data;
      setStats({
        total: data.total_payments || 0,
        paid: data.paid_payments || 0,
        pending: data.pending_payments || 0
      });
    } catch (error) {
      console.error('Failed to fetch payment stats', error);
    }
  };

  const handleVerifyPayment = async (paymentId) => {
    setVerifyingPaymentId(paymentId);
    try {
      const response = await axios.post(`/admin/payments/${paymentId}/verify-stripe-status`);
      const data = response.data || {};

      if (data.status === 'refunded' || data.status === 'partially_refunded') {
        toast.info('Pagamento rimborsato rilevato su Stripe e sincronizzato nel database');
      } else if (data.stripe_payment_status === 'paid') {
        toast.success('Pagamento confermato da Stripe');
      } else {
        toast.info('Verifica completata: pagamento ancora non concluso');
      }

      const refreshedPayments = await fetchPayments();
      await fetchStats();

      if (selectedPayment?.id === paymentId) {
        const refreshed = refreshedPayments.find((payment) => payment.id === paymentId);
        if (refreshed) {
          setSelectedPayment(refreshed);
        }
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.detail || 'Errore durante la verifica del pagamento su Stripe';
      toast.error(errorMessage);
      console.error('Verify payment error:', error);
    } finally {
      setVerifyingPaymentId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: (currency || 'EUR').toUpperCase()
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const normalizedStatus = (status || '').toLowerCase();
    const paidStatuses = new Set(['succeeded', 'completed', 'paid']);
    const failedStatuses = new Set(['failed', 'canceled', 'expired']);

    if (paidStatuses.has(normalizedStatus)) {
      return (
        <span className="inline-flex items-center gap-1 text-green-700 font-medium">
          <CheckCircle className="h-4 w-4" />
          {status}
        </span>
      );
    }

    if (normalizedStatus === 'refunded') {
      return (
        <span className="inline-flex items-center gap-1 text-blue-700 font-medium">
          <RefreshCw className="h-4 w-4" />
          refunded
        </span>
      );
    }

    if (normalizedStatus === 'partially_refunded') {
      return (
        <span className="inline-flex items-center gap-1 text-blue-700 font-medium">
          <RefreshCw className="h-4 w-4" />
          partially_refunded
        </span>
      );
    }

    if (failedStatuses.has(normalizedStatus)) {
      return (
        <span className="inline-flex items-center gap-1 text-red-700 font-medium">
          <XCircle className="h-4 w-4" />
          {status}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1 text-orange-700 font-medium">
        <XCircle className="h-4 w-4" />
        {status || 'pending'}
      </span>
    );
  };

  const openDetails = (payment) => {
    setSelectedPayment(payment);
    setDetailsDialogOpen(true);
  };

  const detailsRows = selectedPayment ? [
    ['ID interno', selectedPayment.id],
    ['Utente ID', selectedPayment.user_id],
    ['Utente', selectedPayment.user_name],
    ['Email utente', selectedPayment.user_email],
    ['Email cliente Stripe', selectedPayment.customer_email],
    ['Tipo pagamento', selectedPayment.payment_type],
    ['Importo', formatAmount(selectedPayment.amount, selectedPayment.currency)],
    ['Valuta', selectedPayment.currency],
    ['Stato interno', selectedPayment.status],
    ['Stato Stripe pagamento', selectedPayment.stripe_payment_status || '-'],
    ['Stato Stripe checkout', selectedPayment.stripe_checkout_status || '-'],
    ['Stato Stripe rimborso', selectedPayment.stripe_refund_status || '-'],
    ['Concluso', selectedPayment.is_concluded ? 'Sì' : 'No'],
    ['Pagato', selectedPayment.is_paid ? 'Sì' : 'No'],
    ['Checkout Session ID', selectedPayment.stripe_checkout_session_id],
    ['Payment Intent ID', selectedPayment.stripe_payment_intent_id || '-'],
    ['Event registration ID', selectedPayment.event_registration_id || '-'],
    ['Quota associativa ID', selectedPayment.quota_associativa_id || '-'],
    ['Attività', selectedPayment.activity_title || '-'],
    ['Periodo quota', selectedPayment.quota_periodo || '-'],
    ['Creato il', formatDate(selectedPayment.created_at)],
    ['Aggiornato il', formatDate(selectedPayment.updated_at)],
    ['Pagato il', formatDate(selectedPayment.paid_at)]
  ] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-20 px-6 max-w-7xl mx-auto pb-12">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="h-6 w-6" />
            <h1 className="text-3xl font-bold">Gestione Pagamenti Stripe</h1>
          </div>
          <p className="text-gray-600">Visualizza tutti i payment intent Stripe e verifica gli esiti non conclusi</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Totale Pagamenti</CardTitle>
              <DollarSign className="h-4 w-4 text-gray-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total}</div>
              <p className="text-xs text-gray-600">Tutti i pagamenti registrati</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pagamenti Completati</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{stats.paid}</div>
              <p className="text-xs text-gray-600">Pagamenti conclusi e pagati</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">In Attesa</CardTitle>
              <XCircle className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{stats.pending}</div>
              <p className="text-xs text-gray-600">Pagamenti in attesa</p>
            </CardContent>
          </Card>
        </div>

        {/* Payments Table */}
        <Card>
          <CardHeader>
            <CardTitle>Lista Pagamenti</CardTitle>
            <CardDescription>Tutti i pagamenti dell&apos;associazione</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Caricamento...</div>
            ) : payments.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nessun pagamento trovato
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data creazione</TableHead>
                      <TableHead>Utente</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Importo</TableHead>
                      <TableHead>Stato interno</TableHead>
                      <TableHead>Stato Stripe</TableHead>
                      <TableHead>Checkout Session</TableHead>
                      <TableHead>Payment Intent</TableHead>
                      <TableHead>Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow
                        key={payment.id}
                        className="cursor-pointer hover:bg-gray-100"
                        onClick={() => openDetails(payment)}
                      >
                        <TableCell>{formatDate(payment.created_at)}</TableCell>
                        <TableCell className="font-medium">
                          {payment.user_name || 'N/A'}
                        </TableCell>
                        <TableCell>{payment.user_email || 'N/A'}</TableCell>
                        <TableCell>
                          {payment.payment_type === 'activity' ? 'Attività' : payment.payment_type === 'quota' ? 'Quota' : 'Altro'}
                        </TableCell>
                        <TableCell>{formatAmount(payment.amount, payment.currency)}</TableCell>
                        <TableCell>{getStatusBadge(payment.status)}</TableCell>
                        <TableCell>{payment.stripe_payment_status || '-'}</TableCell>
                        <TableCell className="max-w-[220px] truncate font-mono text-xs" title={payment.stripe_checkout_session_id}>
                          {payment.stripe_checkout_session_id}
                        </TableCell>
                        <TableCell className="max-w-[220px] truncate font-mono text-xs" title={payment.stripe_payment_intent_id || '-'}>
                          {payment.stripe_payment_intent_id || '-'}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={verifyingPaymentId === payment.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleVerifyPayment(payment.id);
                            }}
                            className="inline-flex items-center gap-2"
                          >
                            <RefreshCw className={`h-4 w-4 ${verifyingPaymentId === payment.id ? 'animate-spin' : ''}`} />
                            Verifica esito pagamento
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Dettagli Payment Intent</DialogTitle>
              <DialogDescription>
                Dati completi del pagamento selezionato
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {detailsRows.map(([label, value]) => (
                <div key={label} className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 border-b pb-2">
                  <span className="text-sm font-semibold text-gray-700">{label}</span>
                  <span className="text-sm text-gray-900 break-all">{value || '-'}</span>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">Stripe Data (JSON completo)</p>
              <pre className="text-xs bg-gray-100 p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-all">
                {selectedPayment?.stripe_data
                  ? JSON.stringify(selectedPayment.stripe_data, null, 2)
                  : 'Nessun dato Stripe disponibile'}
              </pre>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
